/**
 * Relationship Intelligence Graph (RIG) — flagship surface route.
 *
 * Governing record: artifacts/BDSE_RIG_Design_Gate_v1_SEALED.html (Variant B ·
 * Merged Signal Story, sealed 2026-07-22), amendments DG-DISPLAY-1,
 * RIG-MOTION-1, RIG-DENSITY-1, and the wiring rulings. Reached from the primary
 * nav and from the Signal modal's launch button (?signal=<id>).
 *
 *  - ?signal=<id> → LIVE: read get_signal_relationships (v7) + the signal
 *    detail + action history, adapt into a RigViewModel, render real data.
 *  - no signal → the illustrative National Grid example behind the preview
 *    ribbon (a labelled demo for the nav entry).
 */

import { Link, useSearchParams } from "react-router-dom";
import { useReadyAuth } from "@/auth/use-auth";
import { useSignalDetail, useActionHistory } from "@/features/signals/hooks";
import type { ActionHistoryEntry, FeedSignal } from "@/features/signals/types";
import { RigSurface } from "@/features/rig/components/RigSurface";
import { toRigViewModel } from "@/features/rig/adapter";
import { useRigRelationships } from "@/features/rig/live";
import type { RigHistoryInput, RigSignalInput } from "@/features/rig/payload";

function projectSignal(s: FeedSignal): RigSignalInput {
  return {
    headline: s.headline,
    company: s.company,
    signalType: s.signalType,
    eventType: s.eventType,
    verification: s.verification,
    summary: s.content.summary,
    whyItMatters: s.content.why_it_matters,
    recommendedAction: s.content.recommended_action,
    talkTrack: s.content.talk_track,
    firstObservedAt: s.firstObservedAt,
    lastObservedAt: s.lastObservedAt,
    deadlineAt: s.deadlineAt,
    url: s.url,
    ownerName: s.assignedToName ?? s.claimedByName ?? null,
    pursuitStage: s.canonicalStageKey ?? null,
  };
}

function projectHistory(e: ActionHistoryEntry): RigHistoryInput {
  return { verb: e.verb, detail: e.detail, actorName: e.actorName, at: e.at };
}

function RigMessage({ title, body }: { title: string; body: string }): JSX.Element {
  return (
    <div className="sx-rig">
      <div className="rig-panel rig-pad" style={{ maxWidth: 520, marginTop: 8 }}>
        <div className="rig-sec-eyebrow">Relationship Intelligence Graph</div>
        <h2 className="rig-sec-title">{title}</h2>
        <p style={{ fontSize: 13, color: "var(--ink-70)", margin: "8px 0 0" }}>{body}</p>
      </div>
    </div>
  );
}

/** Direct /rig access with no signal: the RIG is signal-anchored only, so point
 *  the user back to Signals rather than render an illustrative story. */
function RigNoSignal(): JSX.Element {
  return (
    <div className="sx-rig">
      <div className="rig-panel rig-pad" style={{ maxWidth: 520, marginTop: 8 }}>
        <div className="rig-sec-eyebrow">Relationship Intelligence Graph</div>
        <h2 className="rig-sec-title">Opens from a signal</h2>
        <p style={{ fontSize: 13, color: "var(--ink-70)", margin: "8px 0 14px" }}>
          The Relationship Intelligence Graph opens from a signal. Choose a signal in Signals and
          launch its relationship graph from the signal's detail card.
        </p>
        <Link to="/signals" className="rig-btn rig-primary" style={{ textDecoration: "none" }}>
          Go to Signals
        </Link>
      </div>
    </div>
  );
}

export function Rig(): JSX.Element {
  const [params] = useSearchParams();
  const signalId = params.get("signal");
  const { user } = useReadyAuth();

  const rel = useRigRelationships(signalId);
  const detail = useSignalDetail(signalId, user.id);
  const historyQ = useActionHistory(signalId);

  if (!signalId) {
    return <RigNoSignal />;
  }
  if (rel.isPending) {
    return (
      <RigMessage
        title="Loading relationship intelligence…"
        body="Resolving this signal's relationship graph."
      />
    );
  }
  if (rel.isError || !rel.data) {
    return (
      <RigMessage
        title="Couldn't load the relationship graph"
        body={
          rel.error instanceof Error
            ? rel.error.message
            : "The relationship service did not return a result."
        }
      />
    );
  }

  const signalInput = detail.data && !detail.isPlaceholderData ? projectSignal(detail.data) : null;
  const historyInput = historyQ.data ? historyQ.data.map(projectHistory) : null;
  const vm = toRigViewModel(rel.data, signalInput, historyInput, { nowMs: Date.now() });
  return <RigSurface key={signalId} vm={vm} signalId={signalId} />;
}
